import type { CSSProperties } from 'react';
import { cn } from '@/utils/cn';
import { getContrastingTextColor } from '@/utils/color-utils';
import { useClusterTuningStore } from '../store/cluster-tuning-store';
import { clusterDiameter, type ClusterStyle } from '../utils/cluster-layout';

const formatCount = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `${n}`;

interface ClusterBubbleProps {
  count: number;
  maxCount: number;
  maxPx?: number;
  variant: ClusterStyle;
  /** Operator / marker_color accent; defaults to the theme ink */
  brandColor?: string;
  /** Share of free plugs (0..1); draws a small bar under the count */
  availableShare?: number;
}

/**
 * Cluster = "many chargers in this area": size carries how many (area ∝
 * count), the number stays, no availability. Deliberately not a pill, so
 * it never reads as a site.
 */
export function ClusterBubble({
  count,
  maxCount,
  maxPx,
  variant,
  brandColor,
  availableShare,
}: ClusterBubbleProps) {
  const sizeExp = useClusterTuningStore((s) => s.sizeExp);
  const d = clusterDiameter(count, maxCount, maxPx, sizeExp);
  const brand = brandColor ?? 'rgb(var(--c-ink))';
  const onBrand = brandColor
    ? getContrastingTextColor(brandColor)
    : 'rgb(var(--c-surface))';
  const fontSize = Math.round(12 + (d - 28) / 6);
  const label = formatCount(count);
  const ring =
    '0 0 0 3px rgb(var(--c-surface)), 0 8px 24px -8px rgba(0,0,0,0.45)';
  const chip = (size: number, fs: number) => (
    <span
      className="grid place-items-center rounded-full px-2 font-bold"
      style={{
        minWidth: size,
        height: size,
        fontSize: fs,
        background: brand,
        color: onBrand,
        boxShadow: '0 0 0 2px rgb(var(--c-surface))',
      }}
    >
      {label}
    </span>
  );

  const box: CSSProperties = { width: d, height: d };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${count} charge points in this area${availableShare === undefined ? '' : `, ${Math.round(availableShare * 100)}% free`}`}
      className={cn(
        'relative grid cursor-pointer place-items-center font-bold tabular-nums tracking-tight transition-transform duration-150 hover:scale-105',
        variant === 'square' && 'rounded-[28%]',
        (variant === 'disc' ||
          variant === 'area' ||
          variant === 'outline' ||
          variant === 'halo') &&
          'rounded-full'
      )}
      style={
        variant === 'square' || variant === 'disc'
          ? {
              ...box,
              fontSize,
              background: brand,
              color: onBrand,
              boxShadow: ring,
            }
          : variant === 'outline'
            ? {
                ...box,
                fontSize,
                color: brand,
                background: 'rgb(var(--c-surface) / 0.92)',
                border: `3px solid ${brand}`,
                boxShadow: '0 8px 24px -10px rgba(0,0,0,0.4)',
              }
            : variant === 'area'
              ? {
                  ...box,
                  background: `color-mix(in srgb, ${brand} 16%, transparent)`,
                  border: `2px solid color-mix(in srgb, ${brand} 70%, transparent)`,
                }
              : {
                  width: d * 1.25,
                  height: d * 1.25,
                  background: `radial-gradient(circle, color-mix(in srgb, ${brand} 32%, transparent) 0 38%, color-mix(in srgb, ${brand} 10%, transparent) 62%, transparent 71%)`,
                }
      }
    >
      {variant === 'area'
        ? chip(30, 13)
        : variant === 'halo'
          ? chip(Math.round(d * 0.42), Math.max(12, Math.round(fontSize * 0.8)))
          : label}
      {availableShare !== undefined && (
        <span
          aria-hidden
          className="absolute bottom-[16%] left-1/2 h-[3px] w-[44%] -translate-x-1/2 overflow-hidden rounded-full"
          style={{
            background: `color-mix(in srgb, ${variant === 'outline' ? brand : onBrand} 25%, transparent)`,
          }}
        >
          <span
            className={cn('block h-full rounded-full', !brandColor && 'bg-ok')}
            // Green clashes with operator colours (e.g. red): use the text colour
            style={{
              width: `${Math.round(availableShare * 100)}%`,
              background: brandColor ? onBrand : undefined,
            }}
          />
        </span>
      )}
    </div>
  );
}
